/**
 * The circuit for a replay's track map (replay-field.ts), by the race's track name: the outline and
 * pit lane the timing learned at a race there, served by a live replay on the server (open to
 * everyone). Every replay has a map: a circuit with no outline yet (not listed, or its learned
 * outline is not the whole lap) gets a schematic loop of its lap length, marked as such, with the
 * cars placed on it from the timing the same way.
 */
import { useEffect, useState } from "react";
import { getPublic } from "../../hooks/useLiveChartData";
import type { TrackOutline } from "../chart/live-field";
import type { PitLane } from "../chart/track-motion";
import type { ReplayTrack } from "./replay-field";

/** A circuit drawn from map data kept with the site (circuits/*.json): metres from the line, the pit road. */
export interface LocalCircuit {
  len: number;
  w: number;
  h: number;
  pts: [number, number][];
  ds: number[];
  pitIn: number;
  pitOut: number;
  road: [number, number][];
}

interface CircuitSource {
  /** The race's track name matches. */
  match: RegExp;
  /** The live replay on the server whose outline and pit lane it uses (when there is no local outline). */
  slug?: string;
  /** The circuit from map data kept with the site, loaded when needed; used instead of the server's. */
  local?: () => Promise<LocalCircuit>;
  /** The credit the map data needs, shown under the map. */
  attribution?: string;
  /** The timing lines between, as shares of the lap (IMSA's IM1, IM2), for races with sector times. */
  sectorShares?: [number, number];
  /** The circuit's lap (m): an outline much shorter than this (MIN_OUTLINE_SHARE) is not the circuit. */
  lapM: number;
}

/** An outline is the circuit only when at least this share of its lap long, with at least MIN_OUTLINE_POINTS points. */
const MIN_OUTLINE_SHARE = 0.8;
const MIN_OUTLINE_POINTS = 50;

/** A learned outline that can be the circuit (the timing can learn one from too few laps). */
export function outlineFits(o: TrackOutline, lapM: number): boolean {
  return o.pts.length >= MIN_OUTLINE_POINTS && o.len >= MIN_OUTLINE_SHARE * lapM;
}

export const REPLAY_CIRCUITS: CircuitSource[] = [
  { match: /sebring/i, slug: "replay-sebring-international-raceway-2026-sunday-7-hour", lapM: 6019 },
  // IMSA's timing lines between at Road Atlanta: IM1 559 m and IM2 2,540 m round a 4,088 m lap.
  // From OpenStreetMap (circuits/road-atlanta.json, ODbL: the credit shows under the map): the
  // racing line and the main pit lane, the line opposite the middle of the pit lane.
  {
    match: /road atlanta/i,
    local: () => import("./circuits/road-atlanta.json").then((m) => m.default as unknown as LocalCircuit),
    attribution: "Circuit © OpenStreetMap contributors",
    sectorShares: [559 / 4088, 2540 / 4088],
    lapM: 4088,
  },
];

/** Lap lengths (m) of circuits with no outline yet, for the schematic; others get SCHEMATIC_LAP_M. */
const LAP_M: [RegExp, number][] = [
  [/barber/i, 3830],
  [/circuit of the americas|\bcota\b/i, 5513],
  [/daytona/i, 5729],
  [/watkins glen/i, 5435],
  [/road america/i, 6515],
  [/laguna seca/i, 3602],
  [/mid-?ohio/i, 3634],
  [/indianapolis/i, 3925],
  [/long beach/i, 3167],
  [/virginia|\bvir\b/i, 5263],
  [/mosport|canadian tire/i, 3957],
  [/lime rock/i, 2459],
  [/sonoma/i, 4052],
  [/thunderhill/i, 4828],
  [/nola|new orleans/i, 4425],
  [/detroit/i, 2735],
];
export const SCHEMATIC_LAP_M = 4000;

/** The lap length for a circuit's schematic (m). */
export function lapLengthFor(trackName: string | null | undefined): number {
  const c = circuitFor(trackName);
  if (c) return c.lapM;
  return (trackName && LAP_M.find(([re]) => re.test(trackName))?.[1]) || SCHEMATIC_LAP_M;
}

/**
 * A schematic loop of this lap length: a rounded rectangle twice as wide as tall, run clockwise,
 * the line halfway along the bottom straight. Points every 10 m or so.
 */
export function schematicOutline(lapM: number): TrackOutline {
  const r = 0.05 * lapM;
  const long = 0.229 * lapM;
  const short = (lapM - 2 * long - 2 * Math.PI * r) / 2;
  const W = long + 2 * r;
  const H = short + 2 * r;
  // Clockwise from the line (bottom middle, heading west on screen: x east, y down).
  type Seg = { len: number; at: (u: number) => [number, number] };
  const arc = (cx: number, cy: number, a0: number): Seg => ({ len: (Math.PI / 2) * r, at: (u) => [cx + r * Math.cos(a0 + (u / r)), cy + r * Math.sin(a0 + (u / r))] });
  const segs: Seg[] = [
    { len: long / 2, at: (u) => [W / 2 - u, H] },
    arc(r, H - r, Math.PI / 2),
    { len: short, at: (u) => [0, H - r - u] },
    arc(r, r, Math.PI),
    { len: long, at: (u) => [r + u, 0] },
    arc(W - r, r, -Math.PI / 2),
    { len: short, at: (u) => [W, r + u] },
    arc(W - r, H - r, 0),
    { len: long / 2, at: (u) => [W - r - u, H] },
  ];
  const n = Math.max(80, Math.round(lapM / 10));
  const pts: [number, number][] = [];
  const ds: number[] = [];
  for (let i = 0; i < n; i++) {
    let d = (i / n) * lapM;
    const at = d;
    let k = 0;
    while (k < segs.length - 1 && d > segs[k].len) d -= segs[k++].len;
    const [x, y] = segs[k].at(Math.min(d, segs[k].len));
    pts.push([Math.round(x * 10) / 10, Math.round(y * 10) / 10]);
    ds.push(Math.round(at * 10) / 10);
  }
  return { len: lapM, w: Math.round(W * 10) / 10, h: Math.round(H * 10) / 10, pts, ds };
}

/** A replay's schematic circuit (no pit lane: stops show in the pit list). */
export function schematicTrack(trackName: string | null | undefined): ReplayTrack {
  const lapM = lapLengthFor(trackName);
  const shares = circuitFor(trackName)?.sectorShares;
  return { outline: schematicOutline(lapM), pit: null, sectors: shares ? [shares[0] * lapM, shares[1] * lapM] : null, schematic: true };
}

export function circuitFor(trackName: string | null | undefined): CircuitSource | null {
  return (trackName && REPLAY_CIRCUITS.find((c) => c.match.test(trackName))) || null;
}

/** The replay's circuit once loaded (its schematic when it has no outline); null for no race, and while loading. */
export function useReplayTrack(trackName: string | null | undefined): ReplayTrack | null {
  const src = circuitFor(trackName);
  const [track, setTrack] = useState<ReplayTrack | null>(null);
  const slug = src?.slug ?? null;
  useEffect(() => {
    setTrack(null);
    if (trackName === null || trackName === undefined) return;
    if (!src || (!slug && !src.local)) {
      setTrack(schematicTrack(trackName));
      return;
    }
    let alive = true;
    if (src.local) {
      src
        .local()
        .then((c) => {
          if (!alive) return;
          const outline: TrackOutline = { len: c.len, w: c.w, h: c.h, pts: c.pts, ds: c.ds };
          const sectors = src.sectorShares ? ([src.sectorShares[0] * c.len, src.sectorShares[1] * c.len] as [number, number]) : null;
          setTrack({ outline, pit: { in: c.pitIn, out: c.pitOut, mph: null, n: 0, road: c.road }, sectors, attribution: src.attribution ?? null });
        })
        .catch(() => {
          if (alive) setTrack(schematicTrack(trackName));
        });
      return () => {
        alive = false;
      };
    }
    if (!slug) return;
    Promise.all([
      getPublic<TrackOutline>(`/live/${encodeURIComponent(slug)}/track`),
      getPublic<{ pit?: PitLane | null }>(`/live/${encodeURIComponent(slug)}/pos`).catch(() => ({ body: { pit: null } })),
    ])
      .then(([o, p]) => {
        if (!alive) return;
        const outline = o.body;
        if (!outlineFits(outline, src.lapM)) {
          setTrack(schematicTrack(trackName));
          return;
        }
        const sectors = src.sectorShares ? ([src.sectorShares[0] * outline.len, src.sectorShares[1] * outline.len] as [number, number]) : null;
        setTrack({ outline, pit: p.body.pit ?? null, sectors });
      })
      .catch(() => {
        if (alive) setTrack(schematicTrack(trackName));
      });
    return () => {
      alive = false;
    };
  }, [slug, trackName]); // eslint-disable-line react-hooks/exhaustive-deps
  return track;
}
